import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  Firestore,
  getDoc,
  getDocs,
  getFirestore,
  query,
  setDoc,
  where,
  orderBy,
  updateDoc,
  limit,
} from 'firebase/firestore/lite';

import { Filesystem } from '@capacitor/filesystem';
import { Network } from '@capacitor/network';
import { FirebaseApp } from 'firebase/app';
import {
  FirebaseStorage,
  getBlob,
  getStorage,
  ref,
  StorageReference,
  uploadBytesResumable,
  uploadString,
  deleteObject,
  getDownloadURL,
} from 'firebase/storage';
import { ST } from 'types';
import { RunModel, RunModelIn } from '../../../models/RunModel/RunModel';
import { UserSN } from '../../../models/User';
import {
  ExploreElementIn,
  ExploreElementOut,
  ExploreElementCreateIn,
} from '../../../models/ExploreElementModel/ExploreElementModel';
import {
  base64ToBuffer,
  blobToBase64,
  getVideoFile,
  getVideoFileInfo,
  trickFromStore,
} from '../../../util';
import { IAuthService } from '../../Authentication';
import { Area, RunArea, ICloudStorage } from '../CloudStorageService';
import i18next from 'i18next';
import { logger } from 'services/Logger';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { connectFirestoreEmulator } from 'firebase/firestore';

export type FilePrefix = 'Play' | 'Record' | 'Video';

const USERS = 'users';

const TRICKS_BY_AREA: Record<Area, RunArea> = {
  test: 'testTricks',
  live: 'prodTricks',
};

const EXPLORE_ELEMENTS_BY_AREA: Record<Area, string> = {
  test: 'testExplore',
  live: 'prodExplore',
};

export class FirebaseCloudStorage implements ICloudStorage {
  db: Firestore;
  app: FirebaseApp;
  auth: IAuthService;
  storage: FirebaseStorage;

  defaultArea: Area;

  constructor(app: FirebaseApp, auth: IAuthService, defaultArea: Area) {
    this.app = app;
    this.db = getFirestore(app);
    this.storage = getStorage(app);
    this.auth = auth;
    this.defaultArea = defaultArea;
  }

  setDefaultArea(area: Area) {
    this.defaultArea = area;
  }

  destroy() {
    logger.debug('FirebaseCloudStorage destroyed');
  }
  // Consntruct the path to the file in the cloud storage in format:
  // {(test|live)/v_1/(Play|Record)}/{user_id}/(Play|Record)_{device_id}_{record_id}.json
  private async getFileRef(
    device_id: string,
    record_id: string,
    playOrRecord: FilePrefix,
    area?: Area,
    fileExtension = 'json'
  ) {
    const _area = area ?? this.defaultArea;
    const userID = (await this.auth.getCurrentUser())?.uid;
    const target = ref(
      this.storage,
      `${_area}/v_1/${playOrRecord}/${userID}/${playOrRecord}_${device_id}_${record_id}.${fileExtension}`
    );
    return target;
  }

  async checkInternetConnection() {
    const status = await Network.getStatus();
    if (!status.connected)
      throw new Error(
        i18next.t('NO_INTERNET_CONNECTION_ERROR', {
          defaultValue: 'Keine Internetverbindung vorhanden!',
        })!
      );
  }

  async downloadPlay(
    device_id_or_url: string,
    record_id?: string,
    area?: Area
  ): Promise<{ data: ST.Play; uri: string }> {
    await this.checkInternetConnection();
    let target: StorageReference;
    if (record_id) {
      target = await this.getFileRef(device_id_or_url, record_id, 'Play', area);
    } else {
      target = ref(this.storage, device_id_or_url);
    }
    const data = await getBlob(target);
    const textData = await data.text();
    return { data: JSON.parse(textData), uri: target.name };
  }

  async downloadVideo(url: string) {
    await this.checkInternetConnection();
    const target = ref(this.storage, url);
    const blobdata = await getBlob(target);

    const fileParts = url.split('/');
    const path = fileParts[fileParts.length - 1]!;
    const directory = getVideoFileInfo('', '').directory;
    const data = await blobToBase64(blobdata);

    // NOTE: The writeFile command will fail for some video files for unknown reasons with the filesystem plugin in version 4.1.1. Once we upgrade to >= 5.1.1 there should be
    // less issues with file saving (and we can delete this comment)
    const result = await Filesystem.writeFile({
      directory,
      path,
      recursive: false,
      data,
    });
    return result.uri;
  }
  // Upload video data from local filesystem to the cloud storage.
  // We assume that files are saved in the data directory of the app
  // in the same format as they are saved in the cloud.
  async uploadVideo(
    deviceId: string,
    recordId: string,
    inBackground: boolean,
    area?: Area
  ) {
    await this.checkInternetConnection();
    const target = await this.getFileRef(
      deviceId!,
      recordId!,
      'Video',
      area,
      'mp4'
    );

    // Read the file from the filesystem.
    // Data is returned as a base64 encoded string, so we need
    // to convert it to a byte array in order to upload it.
    const readFileResult = await getVideoFile(deviceId, recordId);
    const bytesData = base64ToBuffer(
      typeof readFileResult.data === 'string'
        ? readFileResult.data
        : await readFileResult.data.text()
    );
    const uploadTask = uploadBytesResumable(target, bytesData);

    const path = await new Promise<string>((resolve, reject) => {
      let uploadRef: StorageReference;
      const unsubscribe = uploadTask.on('state_changed', {
        next: (task) => {
          if (uploadRef) return;
          uploadRef = task.ref;
          if (inBackground) resolve(uploadRef.fullPath);
        },
        error: reject,
        complete: () => {
          unsubscribe();
          if (!inBackground) resolve(uploadRef.fullPath);
        },
      });
    });

    return path;
  }

  async uploadRecord(record: ST.Record, area?: Area) {
    await this.checkInternetConnection();
    const target = await this.getFileRef(
      record.id_device!,
      record.id_record!,
      'Record',
      area
    );
    const uploadResult = await uploadString(target, JSON.stringify(record));
    return uploadResult.ref.fullPath;
  }

  async uploadPlay(play: ST.Play, area?: Area) {
    await this.checkInternetConnection();
    const target = await this.getFileRef(
      play.id_device!,
      play.id_record!,
      'Play',
      area
    );
    const uploadResult = await uploadString(target, JSON.stringify(play));
    return uploadResult.ref.fullPath;
  }

  async deletePlay(play: ST.Play, area?: Area): Promise<void> {
    await this.checkInternetConnection();
    const target = await this.getFileRef(
      play.id_device!,
      play.id_record!,
      'Play',
      area
    );
    return deleteObject(target);
  }

  // Save a trick record to the firestore database
  async addRun(trick: RunModel): Promise<void> {
    await this.checkInternetConnection();
    const user = await this.auth.getCurrentUser();
    const userId = user?.uid;

    // Check if we are authorized to upload content to the cloud
    if (!userId)
      return Promise.reject(
        new Error('Could not add trick: authorization failed')
      );
    // trick adding should always be in the default Area
    const trickArea = TRICKS_BY_AREA[this.defaultArea];
    const trickRecords = collection(this.db, trickArea);

    if (trick.shareUrl) {
      logger.info(`Run seems to have been already uploaded`);
    } else {
      logger.debug('trickToStore');

      const x = trick.toServer(userId);

      logger.debug('addDoc', x);
      addDoc(trickRecords, x)
        .then((docRef) => {
          trick.setShareURL(docRef.id);
          x.shareUrl = docRef.id;
          setDoc(docRef, x)
            .catch((err) => {
              logger.error(err);
            })
            .then(() => {
              logger.info(`updated record ${docRef.id}`);
            });
        })
        .catch((err) => {
          logger.error(err);
        });
    }
  }

  // Update an existing trick record in the firestore database
  async updateRun(trick: RunModel, area?: Area): Promise<void> {
    await this.checkInternetConnection();
    const user = await this.auth.getCurrentUser();
    const userId = user?.uid;

    // Check if we are authorized to update content in the cloud
    if (!userId) {
      return Promise.reject(
        new Error('Could not update trick: authorization failed')
      );
    }

    // Check if trick has a shareUrl (document ID)
    if (!trick.shareUrl) {
      return Promise.reject(
        new Error('Could not update trick: no document ID found')
      );
    }

    const trickArea = TRICKS_BY_AREA[area ?? this.defaultArea];
    const trickDoc = doc(this.db, trickArea, trick.shareUrl);

    try {
      const trickData = trick.toServer(userId);
      await setDoc(trickDoc, trickData, { merge: true });
      logger.info(`Successfully updated trick ${trick.shareUrl}`);
    } catch (error) {
      logger.error('Failed to update trick:', error);
      throw error;
    }
  }

  async getRunsById(ids: Array<string>, area?: Area): Promise<Array<RunModel>> {
    await this.checkInternetConnection();
    if (ids.length === 0) return [];
    const tricksArea = TRICKS_BY_AREA[area ?? this.defaultArea];
    const col = collection(this.db, tricksArea);
    const q = query(col, where('shareUrl', 'in', ids));

    const resp = await getDocs(q);
    const ret: Array<RunModel> = [];
    await Promise.all(
      resp.docs.map(async (doc) => {
        ret.push(await trickFromStore(doc.data() as RunModelIn, this));
      })
    );
    return ret;
  }

  async getRunsByUser(uid: string, area?: Area): Promise<Array<RunModel>> {
    await this.checkInternetConnection();
    const tricksArea = TRICKS_BY_AREA[area ?? this.defaultArea];
    const col = collection(this.db, tricksArea);
    const q = query(col, where('uid', '==', uid));

    let resp = await getDocs(q);

    const ret: Array<RunModel> = [];
    await Promise.all(
      resp.docs.map(async (doc) => {
        ret.push(await trickFromStore(doc.data() as RunModelIn, this));
      })
    );
    return ret;
  }

  async getNewerPublicRuns(
    refDate?: Date,
    area?: Area
  ): Promise<Array<RunModel>> {
    const tricksArea = TRICKS_BY_AREA[area ?? this.defaultArea];
    const date = refDate || new Date(0);
    const operator = '>';
    return this.getPublicRuns(tricksArea, date, operator);
  }

  async getOlderPublicRuns(
    refDate?: Date,
    area?: Area
  ): Promise<Array<RunModel>> {
    const tricksArea = TRICKS_BY_AREA[area ?? this.defaultArea];
    const date = refDate || new Date();
    const operator = '<';
    return this.getPublicRuns(tricksArea, date, operator);
  }

  async getPublicRuns(
    area: RunArea,
    refDate: Date,
    operator: '>' | '<'
  ): Promise<Array<RunModel>> {
    try {
      await this.checkInternetConnection();

      // First, do a debug query to check actual data types in the collection
      const col = collection(this.db, area);
      const q = query(
        col,
        where('date', operator, refDate || new Date(0)),
        where('isPublic', '==', true),
        orderBy('date', 'desc'),
        limit(5)
      );

      let resp = await getDocs(q);
      return Promise.all(
        resp.docs.map(async (doc) => {
          const docData = doc.data() as RunModelIn;
          return await trickFromStore(docData, this);
        })
      );
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getFriendsRuns(area?: Area): Promise<Array<RunModel>> {
    await this.checkInternetConnection();
    const currentUser = await this.auth.getCurrentUser();
    if (!currentUser) {
      return [];
    }

    const tricksArea = TRICKS_BY_AREA[area ?? this.defaultArea];
    const col = collection(this.db, tricksArea);

    // For now, return all public tricks as "friends" tricks
    // In the future, this could be extended to check actual friend relationships
    const q = query(
      col,
      where('isPublic', '==', true),
      where('uid', '!=', currentUser.uid), // Exclude current user's tricks
      orderBy('uid', 'asc'), // Must order by uid first due to inequality filter
      orderBy('date', 'desc')
    );

    let resp = await getDocs(q);

    const ret: Array<RunModel> = [];
    await Promise.all(
      resp.docs.map(async (doc) => {
        ret.push(await trickFromStore(doc.data() as RunModelIn, this));
      })
    );
    return ret;
  }

  async getRun(id: string, area?: Area): Promise<RunModel | null> {
    await this.checkInternetConnection();
    const tricksArea = TRICKS_BY_AREA[area ?? this.defaultArea];
    const trick = doc(this.db, tricksArea, id);
    let resp = await getDoc(trick);
    if (!resp.exists()) return null;
    return trickFromStore(resp.data() as RunModelIn, this);
  }

  async delRun(id: string, area?: Area): Promise<void> {
    await this.checkInternetConnection();
    const tricksArea = TRICKS_BY_AREA[area ?? this.defaultArea];
    const trick = doc(this.db, tricksArea, id);
    return deleteDoc(trick);
  }

  async getUser(uid: string): Promise<UserSN | null> {
    await this.checkInternetConnection();
    const col = collection(this.db, USERS);
    const resp = await getDoc(doc(col, uid));

    const ret = resp.data() as UserSN;
    ret.id = resp.id;
    return ret;
  }

  async getAllUsers(): Promise<Array<UserSN>> {
    await this.checkInternetConnection();
    const col = collection(this.db, USERS);
    const resp = await getDocs(col);

    const ret: Array<UserSN> = [];
    resp.forEach((doc) => {
      const data = doc.data() as UserSN;
      data.id = doc.id;
      ret.push(data);
    });

    logger.debug('✅ Fetched all users from Firebase:', {
      count: ret.length,
      users: ret.map((user) => ({
        id: user.id,
        uid: user.uid,
        displayName: user.displayName,
      })),
    });

    return ret;
  }

  async putUser(user: UserSN, id?: string): Promise<any> {
    await this.checkInternetConnection();
    const usrDB = collection(this.db, USERS);
    if (id) {
      // this is the way the firebase docs describe how it's done
      //   but it does throw an uncaught error somehow..
      //   It still works, so it'll stay.
      logger.debug('update user in DB', user);
      const ref = doc(usrDB, id);
      return setDoc(ref, user);
    }
    logger.debug('set new user in db');
    const ref = await addDoc(usrDB, user);
    user.setId(ref.id);

    return ref;
  }

  // ExploreElement methods
  async addExploreElement(element: ExploreElementCreateIn): Promise<string> {
    await this.checkInternetConnection();
    const exploreArea = EXPLORE_ELEMENTS_BY_AREA[this.defaultArea];
    const col = collection(this.db, exploreArea);

    // Entferne id, falls sie versehentlich im Objekt ist
    const { id: _id, ...elementWithoutId } = element as any;

    logger.debug('🔥 Adding ExploreElement to Firebase:', {
      collection: exploreArea,
      elementName: element.name,
      elementType: element.type,
    });

    const docRef = await addDoc(col, elementWithoutId);
    logger.debug('✅ ExploreElement successfully added to Firebase:', {
      documentId: docRef.id,
      collection: exploreArea,
    });
    return docRef.id;
  }

  async getExploreElements(area?: Area): Promise<Array<ExploreElementOut>> {
    await this.checkInternetConnection();
    const exploreArea = EXPLORE_ELEMENTS_BY_AREA[area ?? this.defaultArea];
    const col = collection(this.db, exploreArea);

    logger.debug('🔥 Fetching ExploreElements from Firebase:', {
      collection: exploreArea,
      area: area ?? this.defaultArea,
    });

    const resp = await getDocs(col);

    const ret: Array<ExploreElementOut> = [];
    resp.forEach((doc) => {
      const data = doc.data() as ExploreElementOut;
      data.id = doc.id;
      ret.push(data);
    });

    logger.debug('✅ Fetched ExploreElements from Firebase:', {
      count: ret.length,
      collection: exploreArea,
      elements: ret.map((el) => ({ id: el.id, name: el.name, type: el.type })),
    });

    return ret;
  }

  async getExploreElementsByUser(
    uid: string,
    area?: Area
  ): Promise<Array<ExploreElementOut>> {
    await this.checkInternetConnection();
    const exploreArea = EXPLORE_ELEMENTS_BY_AREA[area ?? this.defaultArea];
    const col = collection(this.db, exploreArea);
    const q = query(col, where('uid', '==', uid));
    const resp = await getDocs(q);

    const ret: Array<ExploreElementOut> = [];
    resp.forEach((doc) => {
      const data = doc.data() as ExploreElementOut;
      data.id = doc.id;
      ret.push(data);
    });

    return ret;
  }

  async getExploreElement(
    id: string,
    area?: Area
  ): Promise<ExploreElementOut | null> {
    await this.checkInternetConnection();
    const exploreArea = EXPLORE_ELEMENTS_BY_AREA[area ?? this.defaultArea];
    const elementDoc = doc(this.db, exploreArea, id);
    const resp = await getDoc(elementDoc);

    if (!resp.exists()) return null;

    const data = resp.data() as ExploreElementOut;
    data.id = resp.id;
    return data;
  }

  async updateExploreElement(element: ExploreElementIn): Promise<void> {
    await this.checkInternetConnection();

    const exploreArea = EXPLORE_ELEMENTS_BY_AREA[this.defaultArea];
    const elementRef = doc(this.db, exploreArea, element.id!);

    // ✅ FIXED: Clean data but preserve participant arrays
    const cleanElement = Object.keys(element).reduce((acc, key) => {
      const value = element[key as keyof ExploreElementIn];
      if (value !== undefined && value !== null) {
        // ✅ Special handling for participants array
        if (key === 'participants' && Array.isArray(value)) {
          acc[key] = value.map((participant: any) => ({
            userId: participant.userId,
            userName: participant.userName,
            joinedAt:
              participant.joinedAt instanceof Date
                ? participant.joinedAt
                : new Date(participant.joinedAt),
            runCount: participant.runCount || 0,
            bestScore: participant.bestScore || 0,
            scores: Array.isArray(participant.scores) ? participant.scores : [], // ✅ Ensure scores array
            nationality: participant.nationality || '',
            profileImageUrl: participant.profileImageUrl || '',
            lastRunAt: participant.lastRunAt
              ? participant.lastRunAt instanceof Date
                ? participant.lastRunAt
                : new Date(participant.lastRunAt)
              : null,
          }));
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {} as any);

    // ✅ Add update timestamp
    cleanElement.updatedAt = new Date();
    await updateDoc(elementRef, cleanElement);
  }

  async deleteExploreElement(id: string, area?: Area): Promise<void> {
    await this.checkInternetConnection();
    const exploreArea = EXPLORE_ELEMENTS_BY_AREA[area ?? this.defaultArea];
    const elementDoc = doc(this.db, exploreArea, id);
    await deleteDoc(elementDoc);
    logger.debug('ExploreElement deleted from Firebase:', id);
  }

  async init(
    apiKey: string,
    authDomain: string,
    projectId: string,
    debugConfig?: { useFirebaseEmulator: boolean }
  ) {
    try {
      // Emulator-Verbindung für Development
      const isDevelopment = process.env.NODE_ENV === 'development';
      const useEmulator =
        debugConfig?.useFirebaseEmulator ||
        process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true';

      if (isDevelopment && useEmulator && !this._isEmulatorConnected) {
        try {
          // Firestore Emulator
          if (!this._firestoreEmulatorConnected) {
            connectFirestoreEmulator(this.db, 'localhost', 8080);
            this._firestoreEmulatorConnected = true;
          }

          // Auth Emulator - KORRIGIERTES FORMAT
          const auth = getAuth(this.app);
          if (!this._authEmulatorConnected) {
            // Verwende nur Host und Port, NICHT die komplette URL
            connectAuthEmulator(auth, 'http://localhost:9099', {
              disableWarnings: true,
            });
            this._authEmulatorConnected = true;
          }
        } catch (emulatorError) {
          logger.warn('Emulator connection failed:', emulatorError);
        }

        this._isEmulatorConnected = true;
      }
    } catch (error) {
      throw error;
    }
  }

  private _isEmulatorConnected = false;
  private _firestoreEmulatorConnected = false;
  private _authEmulatorConnected = false;

  async uploadProfilePhoto(userId: string, file: File, area?: Area): Promise<string> {
    const usedArea = area || this.defaultArea;
    logger.info(`[FirebaseCloudStorage] Starte Upload für Profilbild: userId=${userId}, file=${file.name}, area=${usedArea}`);
    const currentUser = await this.auth.getCurrentUser();
    logger.info(`[FirebaseCloudStorage] Aktueller Auth-User:`, currentUser);
    if (!currentUser) {
      logger.error('[FirebaseCloudStorage] Kein eingeloggter User für Profilbild-Upload!');
      throw new Error('Kein eingeloggter User für Profilbild-Upload!');
    }
    await this.checkInternetConnection();
    const path = `${usedArea}/v_1/Images/${userId}/${file.name}`;
    logger.debug(`[FirebaseCloudStorage] Ziel-Pfad für Profilbild: ${path} (Bucket/Area: ${usedArea})`);
    const fileRef = ref(this.storage, path);
    const arrayBuffer = await file.arrayBuffer();
    logger.debug(`[FirebaseCloudStorage] Datei als ArrayBuffer gelesen, Größe: ${arrayBuffer.byteLength} bytes`);
    try {
      const uploadTask = uploadBytesResumable(fileRef, new Uint8Array(arrayBuffer));
      await new Promise<void>((resolve, reject) => {
        uploadTask.on('state_changed', {
          error: (err) => {
            logger.error(`[FirebaseCloudStorage] Fehler beim Upload des Profilbilds (area=${usedArea}):`, err);
            reject(err);
          },
          complete: () => {
            logger.info(`[FirebaseCloudStorage] Upload abgeschlossen für: ${path} (area=${usedArea})`);
            resolve();
          },
        });
      });
      const downloadUrl = await getDownloadURL(fileRef);
      logger.info(`[FirebaseCloudStorage] Download-URL für Profilbild: ${downloadUrl} (area=${usedArea})`);
      return downloadUrl;
    } catch (err) {
      logger.error('[FirebaseCloudStorage] Upload-Fehler (catch):', err);
      throw err;
    }
  }

  // Profilbild-Upload über das Environment (wie bei RunModel)
  async uploadProfilePhotoForUser(user: UserSN, file: File): Promise<string> {
    // Zugriff auf cloudStorage über user.environment (MobX-State-Tree View)
    // @ts-expect-error environment ist ein mobx-state-tree View, aber nicht im Typ UserSN
    const storage = user.environment?.cloudStorage;
    if (!storage || typeof storage.uploadProfilePhoto !== 'function') {
      throw new Error(
        'cloudStorage/uploadProfilePhoto nicht verfügbar im Environment!'
      );
    }
    return storage.uploadProfilePhoto(user.uid, file);
  }
}

export default FirebaseCloudStorage;
